import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useHotkeys } from 'react-hotkeys-hook';
import { useSpaceSkills, useDeleteSpaceSkill } from '../../hooks/useSkills';
import { useEntityPermissions } from '../../hooks/useEntityPermissions';
import { useSpaceId } from '../../contexts/SpaceContext';
import { routes } from '../../lib/routes';
import { Badge, Button, ConfirmModal, Input, Table, getStatusVariant } from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import type { Skill } from '../../types';

export default function SkillList() {
  const navigate = useNavigate();
  const spaceId = useSpaceId();
  const { success, error: showError } = useToast();
  const { data, isLoading } = useSpaceSkills();
  const deleteSkill = useDeleteSpaceSkill();
  const { canDelete } = useEntityPermissions();

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    skill: Skill | null;
  }>({ isOpen: false, skill: null });

  // `Esc` focuses the skill search. Routed via react-hotkeys-hook so it shows
  // up in the Shift+? cheatsheet and inherits its built-in skip for
  // input/textarea/contentEditable targets.
  useHotkeys(
    'escape',
    (event) => {
      event.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    },
    {
      description: 'Focus skill search',
      metadata: { group: 'Skills' },
    },
  );

  const skills = data?.items || [];

  const filteredSkills = skills.filter(
    (skill) =>
      skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      skill.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDelete = async () => {
    if (!deleteModal.skill) return;

    try {
      await deleteSkill.mutateAsync(deleteModal.skill.skillId);
      success('Skill deleted successfully');
      setDeleteModal({ isOpen: false, skill: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete skill');
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (skill: Skill) => (
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-foreground">{skill.name}</p>
            {skill.provenance?.source === 'plugin' && (
              <Badge variant="info">Plugin</Badge>
            )}
          </div>
          <p className="text-muted-foreground text-xs mt-0.5 line-clamp-1">
            {skill.description}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      className: 'whitespace-nowrap',
      render: (skill: Skill) => (
        <Badge variant={getStatusVariant(skill.status)}>{skill.status}</Badge>
      ),
    },
    {
      key: 'updatedAt',
      header: 'Last Updated',
      className: 'whitespace-nowrap',
      render: (skill: Skill) => (
        <span className="text-muted-foreground">
          {new Date(skill.updatedAt).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (skill: Skill) => (
        <div className="flex items-center gap-2">
          <Link to={routes.console.spaceSkillEdit(spaceId, skill.skillId)}>
            <Button variant="ghost" size="sm">
              Edit
            </Button>
          </Link>
          {canDelete(skill) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteModal({ isOpen: true, skill });
              }}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
            >
              Delete
            </Button>
          )}
        </div>
      ),
      className: 'text-right whitespace-nowrap',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Skills</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Reusable logic modules that can be attached to agents
          </p>
        </div>
        <Link to={routes.console.spaceSkillNew(spaceId)}>
          <Button>Create Skill</Button>
        </Link>
      </div>

      {/* Search */}
      <div className="flex gap-4">
        <div className="flex-1 max-w-md">
          <Input
            ref={searchInputRef}
            placeholder="Search skills..."
            value={searchQuery}
            onChange={(e: { target: { value: string } }) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Skills table */}
      <Table
        columns={columns}
        data={filteredSkills}
        keyExtractor={(skill) => skill.skillId}
        onRowClick={(skill) => navigate(routes.console.spaceSkill(spaceId, skill.skillId))}
        emptyMessage="No skills found. Create your first skill to get started."
        isLoading={isLoading}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, skill: null })}
        onConfirm={handleDelete}
        title="Delete Skill"
        message={`Are you sure you want to delete "${deleteModal.skill?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteSkill.isPending}
      />
    </div>
  );
}
